import React from 'react'
import { Box, Typography, makeStyles, Theme } from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        width: '100%',
        height: '100%',
        minHeight: 96,
        padding: theme.spacing(2),
        border: '1px solid #C8CED6',
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        transition:
            'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main + '08',
            boxShadow: '0 2px 8px rgba(16, 24, 40, 0.08)',
            '& $chevron': {
                opacity: 1,
                transform: 'translateX(0)',
            },
        },
        '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
        '&:disabled': {
            opacity: 0.55,
            cursor: 'not-allowed',
            boxShadow: 'none',
        },
    },
    cardMuted: {
        borderStyle: 'dashed',
        borderColor: '#B0B7C3',
        backgroundColor: theme.palette.grey[50],
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.palette.grey[100],
            borderColor: theme.palette.text.secondary,
        },
    },
    iconWrap: {
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
    },
    body: {
        minWidth: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    title: {
        fontWeight: 700,
        fontSize: '0.9375rem',
        lineHeight: 1.3,
    },
    description: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        lineHeight: 1.35,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    originSlot: {
        marginTop: 2,
        minWidth: 0,
    },
    meta: {
        color: theme.palette.text.secondary,
        fontSize: '0.6875rem',
        lineHeight: 1.3,
        marginTop: 2,
        fontWeight: 600,
    },
    chevron: {
        flexShrink: 0,
        color: theme.palette.text.secondary,
        opacity: 0,
        transform: 'translateX(-4px)',
        transition: 'opacity 120ms ease, transform 120ms ease',
    },
}))

export interface ConfigOptionCardProps {
    title: string
    description?: string | null
    /** Origin icon + name (e.g. org template this was copied from). */
    origin?: React.ReactNode
    /** Secondary line — e.g. modified date. */
    meta?: string | null
    icon?: React.ReactNode
    iconColor?: string
    iconBackground?: string
    muted?: boolean
    disabled?: boolean
    onClick: () => void
}

export const ConfigOptionCard: React.FC<ConfigOptionCardProps> = ({
    title,
    description,
    origin,
    meta,
    icon,
    iconColor,
    iconBackground,
    muted = false,
    disabled = false,
    onClick,
}) => {
    const classes = useStyles()

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`${classes.card}${muted ? ` ${classes.cardMuted}` : ''}`}
        >
            {icon ? (
                <Box
                    className={classes.iconWrap}
                    style={{
                        color: iconColor,
                        backgroundColor: iconBackground,
                    }}
                >
                    {icon}
                </Box>
            ) : null}
            <Box className={classes.body}>
                <Typography className={classes.title}>{title}</Typography>
                {origin ? (
                    <Box className={classes.originSlot}>{origin}</Box>
                ) : null}
                {description ? (
                    <Typography className={classes.description}>
                        {description}
                    </Typography>
                ) : null}
                {meta ? (
                    <Typography className={classes.meta}>{meta}</Typography>
                ) : null}
            </Box>
            <ChevronRightIcon className={classes.chevron} fontSize="small" />
        </button>
    )
}
